import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PictureAsPdf as PdfIcon,
  Photo as PhotoIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchManualViewer } from '../../store/slices/manualsSlice';
import { PDFViewer } from '../../components/manuals/PDFViewer';
import { PhotoManualViewer } from '../../components/manuals/PhotoManualViewer';
import { format, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

// Helper function to safely format dates
const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString || dateString === 'null' || dateString === 'undefined') {
    return 'Nicht verfügbar';
  }
  
  try {
    let date: Date;
    
    if (typeof dateString === 'string') {
      if (dateString.trim() === '' || dateString === '0000-00-00T00:00:00Z') {
        return 'Nicht verfügbar';
      }
      date = parseISO(dateString);
    } else {
      date = new Date(dateString as any);
    }
    
    if (!isValid(date) || date.getFullYear() < 1900) {
      return 'Ungültiges Datum';
    }
    
    return format(date, 'dd.MM.yyyy HH:mm', { locale: de });
  } catch (error) {
    console.warn('Error formatting date in ManualDetailPage:', dateString, error);
    return 'Ungültiges Datum';
  }
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

export const ManualDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentManual, isLoading, error } = useAppSelector((state) => state.manuals);
  const { user } = useAppSelector((state) => state.auth);
  const [tabValue, setTabValue] = React.useState(0);

  const canEdit = user?.role === 'admin';

  useEffect(() => {
    if (id) {
      dispatch(fetchManualViewer(id));
    }
  }, [dispatch, id]);

  const handleDownload = async () => {
    if (currentManual && currentManual.type === 'pdf') {
      try {
        // Use authenticated API call to get PDF
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/v1/manuals/${currentManual.id}/pdf`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${currentManual.title}.pdf`;
          link.click();
          URL.revokeObjectURL(url);
        } else {
          console.error('Failed to download PDF:', response.statusText);
        }
      } catch (error) {
        console.error('Download error:', error);
      }
    }
  };

  const handleGoBack = () => {
    // Navigate back to the machine detail page with the manuals tab active
    if (currentManual?.machines && currentManual.machines.length > 0) {
      // Navigate to the first associated machine
      navigate(`/machines/${currentManual.machines[0].id}?tab=manuals`);
    } else {
      // Fallback to dashboard if no machines are associated
      navigate('/');
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !currentManual) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error || 'Anleitung konnte nicht geladen werden'}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={handleGoBack}
          sx={{ mt: 2 }}
        >
          Zurück
        </Button>
      </Box>
    );
  }

  const getPdfUrl = () => {
    return `/api/v1/manuals/${currentManual.id}/pdf`;
  };

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={handleGoBack}
          sx={{
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Maschinen
        </Link>
        <Typography color="text.primary">
          {currentManual.title}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Button
                startIcon={<BackIcon />}
                onClick={handleGoBack}
                variant="outlined"
              >
                Zurück
              </Button>
              {currentManual.type === 'pdf' ? (
                <PdfIcon color="error" sx={{ fontSize: 32 }} />
              ) : (
                <PhotoIcon color="primary" sx={{ fontSize: 32 }} />
              )}
            </Box>
            
            <Typography variant="h4" component="h1" gutterBottom>
              {currentManual.title}
            </Typography>
            
            <Box display="flex" gap={1} mb={2}>
              <Chip
                label={currentManual.type.toUpperCase()}
                color={currentManual.type === 'pdf' ? 'error' : 'primary'}
                size="small"
              />
              {currentManual.page_count && (
                <Chip
                  label={`${currentManual.page_count} Seiten`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              Erstellt: {formatDate(currentManual.created_at)}
            </Typography>
            {currentManual.updated_at !== currentManual.created_at && (
              <Typography variant="body2" color="text.secondary">
                Zuletzt bearbeitet: {formatDate(currentManual.updated_at)}
              </Typography>
            )}
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }}>
            <Box display="flex" gap={2} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              {currentManual.type === 'pdf' && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                >
                  Herunterladen
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/manuals/${currentManual.id}/edit`)}
                >
                  Bearbeiten
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Manual Content */}
      <Paper sx={{ overflow: 'hidden' }}>
        {currentManual.type === 'pdf' ? (
          <Box sx={{ height: '80vh' }}>
            <PDFViewer
              url={getPdfUrl()}
              title={currentManual.title}
              onDownload={handleDownload}
            />
          </Box>
        ) : (
          <Box>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              <Tab label="Seitenansicht" />
              <Tab label="Galerie" />
            </Tabs>
            
            <TabPanel value={tabValue} index={0}>
              <PhotoManualViewer
                manual={currentManual}
                viewMode="pages"
              />
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <PhotoManualViewer
                manual={currentManual}
                viewMode="gallery"
              />
            </TabPanel>
          </Box>
        )}
      </Paper>
    </Box>
  );
};